import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import EpcScanner from "../components/EpcScanner";
import LoadingSpinner from "../components/LoadingSpinner";
import Modal from "../components/Modal";
import ProductPicker from "../components/ProductPicker";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { fmtUtc } from "../lib/format";
import {
  type ItemHit,
  type TagMatch,
  type TagStatus,
  type TagStatusRow,
  bulkRetrieveTags,
  listTagStatuses,
  matchTag,
  retrieveTag,
  unmatchTag,
} from "../services/tagApi";

const PAGE_SIZE = 50;

const STATUS_PILL: Record<TagStatus, string> = {
  ON_HAND:     "bg-success-100 text-success-700",
  CHECKED_OUT: "bg-warning-200 text-warning-900",
  PENDING:     "bg-secondary-100 text-secondary-700",
  DEPARTED:    "bg-warning-100 text-warning-700",
  RETRIEVED:   "bg-primary-100 text-primary-700",
  UNKNOWN:     "bg-error-100 text-error-700",
};

const STATUS_OPTIONS: { value: "" | TagStatus; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "ON_HAND", label: "On hand" },
  { value: "PENDING", label: "Pending (never scanned)" },
  { value: "DEPARTED", label: "Departed" },
  { value: "RETRIEVED", label: "Retrieved" },
];

/**
 * Manage Tags — search the EPC→product matches, re-assign or remove them
 * (vendor + platform only). Backed by mttr.rfid_tag: 'import' rows are the WMS
 * seed, 'app' rows were made here. Editing/removing writes only to _jcox.
 */
export const ManageTagsPage: React.FC = () => {
  const { token } = useAuth();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | TagStatus>("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ total: number; tags: TagStatusRow[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit modal state. The edit modal still operates on the TagMatch
  // shape; for the edit path we project the status row down.
  const [editing, setEditing] = useState<TagMatch | null>(null);
  const [editItem, setEditItem] = useState<ItemHit | null>(null);
  const [editQty, setEditQty] = useState("");
  const [saving, setSaving] = useState(false);

  // Unmatch confirm state.
  const [removing, setRemoving] = useState<TagStatusRow | null>(null);

  // Retrieval state.
  const [retrieving, setRetrieving] = useState<TagStatusRow | null>(null);
  const [retrieveReason, setRetrieveReason] = useState("");
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkResult, setBulkResult] = useState<
    { requested: number; succeeded: number; failures: { epc: string; reason: string }[] } | null
  >(null);

  // Ignore stale/out-of-order responses: a slow earlier query must not
  // overwrite the results of a newer one (see ProductPicker for the pattern).
  const reqIdRef = useRef(0);

  const refresh = useCallback(() => {
    if (!token) return;
    const id = ++reqIdRef.current;
    setLoading(true);
    listTagStatuses(token, {
      q: query.trim() || undefined,
      // A status filter is only sent WITH a search term. A status-only query
      // would scan the whole ~900k-row tag view; the dropdown is disabled
      // until there's a term, and this drops it defensively even if not.
      status: (query.trim() ? statusFilter : "") || undefined,
      page, pageSize: PAGE_SIZE,
    })
      .then((res) => {
        if (id !== reqIdRef.current) return;
        setData({ total: res.total, tags: res.tags });
        setBulkSelected((prev) => {
          // Drop selections whose epc is no longer on this page —
          // keeps the bulk action coherent across page changes.
          const nextEpcs = new Set(res.tags.map((t) => t.epc));
          const next = new Set<string>();
          prev.forEach((epc) => { if (nextEpcs.has(epc)) next.add(epc); });
          return next;
        });
        setError(null);
      })
      .catch((e) => { if (id === reqIdRef.current) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (id === reqIdRef.current) setLoading(false); });
  }, [token, query, statusFilter, page]);

  // Debounce search; reset to page 1 when the query changes.
  useEffect(() => {
    const t = setTimeout(refresh, 300);
    return () => clearTimeout(t);
  }, [refresh]);

  // A status filter is only meaningful (and only bounded) with a search term.
  const searching = query.trim().length > 0;
  // Clearing the search reverts the status filter to "All", so we never issue
  // a status-only query against the whole tag table.
  useEffect(() => {
    if (!searching && statusFilter) setStatusFilter("");
  }, [searching, statusFilter]);

  const openEdit = (r: TagStatusRow): void => {
    // Project the status row down to the TagMatch shape the edit modal
    // expects. The quantity comes from mttr.rfid_tag via vw_tag_status;
    // pre-fill the input so the operator sees + edits the CURRENT value
    // instead of retyping it blind.
    setEditing({
      epc: r.epc, item_id: r.item_id, sku: r.sku, name: r.name,
      quantity: r.quantity, source: "app",
      matched_by: null, matched_utc: null,
    });
    setEditItem(null);
    setEditQty(r.quantity != null ? String(r.quantity) : "");
  };

  const doRetrieveOne = async (r: TagStatusRow): Promise<void> => {
    if (!token) return;
    setSaving(true);
    try {
      const out = await retrieveTag(r.epc, token, retrieveReason.trim() || undefined);
      if (!out.ok) {
        setError(out.reason ?? "Retrieval refused.");
      } else {
        setRetrieving(null);
        setRetrieveReason("");
        refresh();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const doBulkRetrieve = async (): Promise<void> => {
    if (!token || bulkSelected.size === 0) return;
    setSaving(true);
    try {
      const out = await bulkRetrieveTags(
        Array.from(bulkSelected), token, retrieveReason.trim() || undefined,
      );
      const failures = out.results
        .filter((r) => !r.ok)
        .map((r) => ({ epc: r.epc, reason: r.reason ?? "refused" }));
      setBulkResult({ requested: out.requested, succeeded: out.succeeded, failures });
      setBulkConfirm(false);
      setBulkSelected(new Set());
      setRetrieveReason("");
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleSelected = (epc: string): void => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(epc)) next.delete(epc);
      else next.add(epc);
      return next;
    });
  };

  const eligibleOnPage = (data?.tags ?? []).filter((t) => t.status === "ON_HAND");
  const allEligibleSelected =
    eligibleOnPage.length > 0 && eligibleOnPage.every((t) => bulkSelected.has(t.epc));
  const toggleSelectAll = (): void => {
    if (allEligibleSelected) {
      setBulkSelected(new Set());
    } else {
      const next = new Set<string>();
      eligibleOnPage.forEach((t) => next.add(t.epc));
      setBulkSelected(next);
    }
  };

  const saveEdit = async (): Promise<void> => {
    if (!editing || !token) return;
    const itemId = editItem?.item_id ?? editing.item_id;
    const q = editQty.trim() === "" ? null : Number(editQty);
    if (q !== null && (!Number.isInteger(q) || q < 0)) {
      setError("Quantity must be a whole number (0 or more), or blank.");
      return;
    }
    setSaving(true);
    try {
      await matchTag(editing.epc, itemId, q, token);
      setEditing(null);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!removing || !token) return;
    setSaving(true);
    try {
      await unmatchTag(removing.epc, token);
      setRemoving(null);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="p-4 md:p-6">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Manage Tags</h1>
          <p className="text-xs text-secondary-500 mt-1">
            Search EPC → product matches; re-assign or remove them.
          </p>
        </div>
        <Link to="/tags/match" className="text-xs text-primary-600 hover:underline">
          + Match a tag
        </Link>
      </header>

      {/* Scan a tag (R6 / camera / manual) to jump straight to its match. */}
      <div className="mb-3">
        <EpcScanner
          onEpc={(epc) => {
            setQuery(epc);
            setPage(1);
          }}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-2 md:items-end md:max-w-2xl mb-3">
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          placeholder="…or search by EPC, SKU, or name"
          className="flex-1 border border-secondary-300 rounded px-3 py-2 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as "" | TagStatus); setPage(1); }}
          disabled={!searching}
          title={searching
            ? "Filter by status"
            : "Search by EPC, SKU, or name to filter by status"}
          className="border border-secondary-300 rounded px-2 py-2 text-sm bg-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}

      {bulkSelected.size > 0 && (
        <div className="mb-3 flex items-center gap-3 text-sm bg-primary-50 border border-primary-200 px-3 py-2 rounded">
          <span>{bulkSelected.size} selected</span>
          <Button size="sm" variant="outline" onClick={() => setBulkSelected(new Set())}>
            Clear
          </Button>
          <Button size="sm" onClick={() => setBulkConfirm(true)}>
            Retrieve selected
          </Button>
        </div>
      )}

      {loading && !data ? (
        <LoadingSpinner label="loading tags" />
      ) : (
        <>
          <div className="text-xs text-secondary-500 mb-2">
            {searching
              ? `${(data?.total ?? 0).toLocaleString()} match${data?.total === 1 ? "" : "es"}`
              : `Showing the first ${data?.tags.length ?? 0} tags — search by EPC, SKU, or name to filter or page through more`}
          </div>
          <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary-50 text-secondary-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={allEligibleSelected}
                      onChange={toggleSelectAll}
                      disabled={eligibleOnPage.length === 0}
                      title={
                        eligibleOnPage.length === 0
                          ? "no ON_HAND tags on this page"
                          : "select all ON_HAND on this page"
                      }
                    />
                  </th>
                  <th className="text-left px-3 py-2">EPC</th>
                  <th className="text-left px-3 py-2">Product</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Location</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data?.tags ?? []).map((t) => {
                  const canRetrieve = t.status === "ON_HAND";
                  return (
                    <tr key={t.epc} className="border-t border-secondary-100">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={bulkSelected.has(t.epc)}
                          onChange={() => toggleSelected(t.epc)}
                          disabled={!canRetrieve}
                          title={canRetrieve ? "select for bulk retrieve" : "only ON_HAND tags can be retrieved"}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{t.epc}</td>
                      <td className="px-3 py-2">
                        <div className="font-mono">{t.sku ?? `#${t.item_id}`}</div>
                        {t.name && <div className="text-xs text-secondary-500">{t.name}</div>}
                        {t.vendor_name && (
                          <div className="text-[11px] text-secondary-400">{t.vendor_name}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {t.quantity != null ? t.quantity : (
                          <span className="text-secondary-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={"text-[11px] px-1.5 py-0.5 rounded " + STATUS_PILL[t.status]}>
                          {t.status}
                        </span>
                        {t.last_event_utc && (
                          <div className="text-[10px] text-secondary-500 mt-0.5">
                            {fmtUtc(t.last_event_utc)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-secondary-700">
                        {t.current_smartbox_code ? (
                          <>
                            <div className="font-medium">{t.current_smartbox_code}</div>
                            {t.current_scu_name && (
                              <div className="text-[10px] text-secondary-400 font-mono">
                                {t.current_scu_name}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-secondary-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openEdit(t)}
                          className="text-xs text-primary-600 hover:underline"
                        >
                          edit
                        </button>
                        {canRetrieve && (
                          <button
                            type="button"
                            onClick={() => { setRetrieving(t); setRetrieveReason(""); }}
                            className="ml-3 text-xs text-primary-600 hover:underline"
                          >
                            retrieve
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setRemoving(t)}
                          className="ml-3 text-xs text-error-600 hover:underline"
                        >
                          remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {data && data.tags.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-secondary-500">
                      No matches found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {searching && totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 text-sm">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Prev
              </Button>
              <span className="text-xs text-secondary-500">
                Page {page} of {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </Button>
            </div>
          )}
        </>
      )}

      {/* Edit (re-assign / quantity) */}
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Edit tag match"
        widthClass="max-w-lg"
      >
        {editing && (
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-secondary-500">Tag</div>
              <div className="font-mono">{editing.epc}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-secondary-500 mb-1">
                Product{" "}
                <span className="normal-case font-normal text-secondary-400">
                  (currently {editing.sku ?? `#${editing.item_id}`})
                </span>
              </div>
              {editItem ? (
                <div className="flex items-center justify-between gap-2 border border-primary-200 bg-primary-50 rounded px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-mono text-sm">{editItem.sku ?? `#${editItem.item_id}`}</div>
                    {editItem.name && (
                      <div className="text-xs text-secondary-500 truncate">{editItem.name}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditItem(null)}
                    className="text-xs text-secondary-500 hover:underline shrink-0"
                  >
                    change
                  </button>
                </div>
              ) : (
                <ProductPicker
                  token={token ?? ""}
                  onSelect={setEditItem}
                  placeholder="Search to re-assign (leave blank to keep current)…"
                />
              )}
            </div>
            <div className="flex items-end gap-3">
              <div>
                <label className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
                  Quantity / tag
                </label>
                <input
                  type="number"
                  min={0}
                  value={editQty}
                  onChange={(e) => setEditQty(e.target.value)}
                  className="w-24 border border-secondary-300 rounded px-2 py-1.5 text-sm text-right"
                />
              </div>
              <div className="ml-auto flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button type="button" size="sm" disabled={saving} onClick={() => void saveEdit()}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Remove confirm */}
      <Modal
        open={!!removing}
        onClose={() => setRemoving(null)}
        title="Remove tag match"
        widthClass="max-w-md"
      >
        {removing && (
          <div className="space-y-4">
            <p className="text-sm text-secondary-700">
              Remove the match for <span className="font-mono">{removing.epc}</span> (
              {removing.sku ?? `#${removing.item_id}`})? The tag will read as unmatched until
              re-assigned.
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setRemoving(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={saving}
                onClick={() => void confirmRemove()}
              >
                {saving ? "Removing…" : "Remove"}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Single retrieve confirm */}
      <Modal
        open={!!retrieving}
        onClose={() => { if (!saving) setRetrieving(null); }}
        title="Retrieve tag"
        widthClass="max-w-md"
      >
        {retrieving && (
          <div className="space-y-4">
            <p className="text-sm text-secondary-700">
              Remove <span className="font-mono">{retrieving.epc}</span> (
              {retrieving.sku ?? `#${retrieving.item_id}`}) from{" "}
              <strong>{retrieving.current_smartbox_code ?? "the current smartbox"}</strong>{" "}
              <em>without</em> recording a customer departure / consumption?
            </p>
            <p className="text-xs text-secondary-500">
              A manual RETRIEVAL ledger entry is recorded for the audit trail. The tag's
              status flips to RETRIEVED; if scanned again it returns to ON_HAND automatically.
            </p>
            <label className="block">
              <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
                Reason (optional)
              </span>
              <input
                value={retrieveReason}
                onChange={(e) => setRetrieveReason(e.target.value)}
                placeholder="e.g. defective, restocking, ..."
                className="w-full border border-secondary-300 rounded px-2 py-1.5 text-sm"
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button
                type="button" variant="outline" size="sm"
                disabled={saving}
                onClick={() => setRetrieving(null)}
              >
                Cancel
              </Button>
              <Button
                type="button" size="sm" disabled={saving}
                onClick={() => void doRetrieveOne(retrieving)}
              >
                {saving ? "Retrieving…" : "Retrieve"}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk retrieve confirm */}
      <Modal
        open={bulkConfirm}
        onClose={() => { if (!saving) setBulkConfirm(false); }}
        title={`Retrieve ${bulkSelected.size} tag${bulkSelected.size === 1 ? "" : "s"}`}
        widthClass="max-w-md"
      >
        <div className="space-y-4">
          <p className="text-sm text-secondary-700">
            About to retrieve {bulkSelected.size} ON_HAND tag
            {bulkSelected.size === 1 ? "" : "s"}. Each becomes a manual RETRIEVAL ledger
            entry — no customer departure / consumption is recorded.
          </p>
          <label className="block">
            <span className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
              Reason (optional, applied to all)
            </span>
            <input
              value={retrieveReason}
              onChange={(e) => setRetrieveReason(e.target.value)}
              placeholder="e.g. recall, end-of-life sweep, ..."
              className="w-full border border-secondary-300 rounded px-2 py-1.5 text-sm"
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button
              type="button" variant="outline" size="sm"
              disabled={saving}
              onClick={() => setBulkConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              type="button" size="sm" disabled={saving}
              onClick={() => void doBulkRetrieve()}
            >
              {saving ? "Retrieving…" : "Retrieve all"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk-retrieve result summary */}
      <Modal
        open={!!bulkResult}
        onClose={() => setBulkResult(null)}
        title="Retrieval result"
        widthClass="max-w-lg"
      >
        {bulkResult && (
          <div className="space-y-3">
            <p className="text-sm">
              {bulkResult.succeeded} of {bulkResult.requested} tag
              {bulkResult.requested === 1 ? "" : "s"} retrieved.
            </p>
            {bulkResult.failures.length > 0 && (
              <div className="border border-warning-200 bg-warning-50 rounded p-2 text-xs">
                <div className="font-semibold text-warning-800 mb-1">
                  {bulkResult.failures.length} refused
                </div>
                <ul className="space-y-1">
                  {bulkResult.failures.slice(0, 12).map((f) => (
                    <li key={f.epc}>
                      <span className="font-mono">{f.epc}</span>: {f.reason}
                    </li>
                  ))}
                  {bulkResult.failures.length > 12 && (
                    <li>… +{bulkResult.failures.length - 12} more</li>
                  )}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setBulkResult(null)}>OK</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ManageTagsPage;
